import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingWrapper from 'components/LoadingWrapper';
import intl from 'i18n/intl';
import { isEqual } from 'lodash';
import { userSelector } from 'slices/auth.slice';
import {
  fetchOrganization,
  organizationLoadingSelector,
  organizationSelector,
  saveOrganization,
} from 'slices/organization.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { EAppId, getRedirectUri } from '@timeedit/types';
import { EAppEnv } from '@timeedit/types/lib/enums/appEnv.enum';
import {
  Alert,
  Button,
  DefaultContentLayout,
  TEInput,
  TopBarContent,
  TopBarLayout,
  Typography,
} from '@timeedit/ui-components';
import { ColorPicker, Form } from '@timeedit/ui-components/antd';
import './branding.scss';

const language: any = intl.messages;

const Branding = () => {
  const appEnv =
    (import.meta.env.VITE_TE_APP_ENV as EAppEnv) ?? EAppEnv.PRODUCTION;
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(userSelector);
  const storeOrganization = useAppSelector(organizationSelector);
  const organizationLoading = useAppSelector(organizationLoadingSelector);

  const [localOrganization, setLocalOrganization] = useState(storeOrganization);

  const isEditable = useMemo(() => {
    return authUser?.scopes?.includes('TE_ROOT_USER') ?? false;
  }, [authUser]);

  useEffect(() => {
    dispatch(fetchOrganization());
  }, [dispatch]);

  useEffect(() => {
    setLocalOrganization(storeOrganization);
  }, [storeOrganization]);

  const onSaveChanges = useCallback(() => {
    dispatch(saveOrganization(localOrganization));
  }, [dispatch, localOrganization]);

  const onDiscardChanges = useCallback(() => {
    setLocalOrganization(storeOrganization);
  }, [storeOrganization]);

  const onChange = useCallback((prop: string, value: string | null) => {
    setLocalOrganization((prev: any) => ({ ...prev, [prop]: value }));
  }, []);

  const hasChanges = !isEqual(localOrganization, storeOrganization);
  const organization = isEditable ? localOrganization : storeOrganization;

  const actions = isEditable
    ? [
        <Button key="discard_changes" size="middle" onClick={onDiscardChanges}>
          {language.discard_changes}
        </Button>,
        <Button
          key="save_changes"
          type="primary"
          size="middle"
          onClick={onSaveChanges}
          loading={organizationLoading}
          disabled={!hasChanges}
        >
          {language.save_changes}
        </Button>,
      ]
    : [];

  const linkToViewerLogo = useMemo(() => {
    return `${getRedirectUri(
      EAppId.TE_VIEWER,
      organization?.customerSignature,
      appEnv
    )}/1/config.html`;
  }, []);

  const linkToCurriculumLogo = useMemo(() => {
    return `${getRedirectUri(
      EAppId.TE_CURRICULUM,
      organization?.customerSignature,
      appEnv
    )}/admin/config`;
  }, []);

  return (
    <DefaultContentLayout
      title={language.branding_title}
      subtitle={language.branding_subtitle}
      actions={actions}
    >
      <LoadingWrapper isLoading={!organization}>
        {organization && (
          <Form layout="vertical" form={form} className="brandingform">
            <Typography.Title level={4} semanticLevel={4}>
              {language.logo}
            </Typography.Title>
            <Form.Item>
              {isEditable ? (
                <TEInput.Input
                  label={language.logo}
                  tooltip={language.logo_tooltip}
                  value={organization.customerLogo || null}
                  onChange={(val) =>
                    onChange('customerLogo', val ? encodeURI(val) : null)
                  }
                />
              ) : (
                <TEInput.PureText
                  label={language.logo}
                  tooltip={language.logo_tooltip}
                  value={organization.customerLogo || null}
                />
              )}
            </Form.Item>
            <Alert showIcon type="info" title={language.logo_help} />
            <Form.Item>
              <TEInput.LabelWrapper label={language.logoViewer}>
                <Typography.Link href={linkToViewerLogo} openInNewTab>
                  {language.logoViewerLink}
                </Typography.Link>
              </TEInput.LabelWrapper>
            </Form.Item>
            <Form.Item>
              <TEInput.LabelWrapper label={language.logoCurriculum}>
                <Typography.Link href={linkToCurriculumLogo} openInNewTab>
                  {language.logoCurriculumLink}
                </Typography.Link>
              </TEInput.LabelWrapper>
            </Form.Item>
            <Typography.Text>Preview of Logo</Typography.Text>
            <div style={{ zIndex: 0 }}>
              <TopBarLayout
                currentProduct={EAppId.TE_ADMIN}
                slots={{
                  left: (
                    <>
                      <TopBarContent.OrganizationLogo
                        organization={organization}
                      />
                      <TopBarContent.OrganizationName
                        organization={organization}
                      />
                    </>
                  ),
                }}
              />
            </div>
            <br />
            <Typography.Title level={4} semanticLevel={4}>
              {language.brand_color}
            </Typography.Title>
            <Form.Item>
              {isEditable ? (
                <>
                  <TEInput.LabelWrapper label={language.brand_color}>
                    <div>
                      <ColorPicker
                        format="hex"
                        disabledFormat
                        disabledAlpha
                        defaultValue={organization.customerColor || null}
                        allowClear
                        showText={(color) => presentHex(color.toHex())}
                        onChange={(color) =>
                          onChange(
                            'customerColor',
                            color ? convertToValidHex(color.toHex()) : null
                          )
                        }
                      />
                    </div>
                  </TEInput.LabelWrapper>
                </>
              ) : (
                <TEInput.PureText
                  label={language.brand_color}
                  value={organization.customerColor || null}
                />
              )}
            </Form.Item>
            <Alert showIcon type="info" title={language.brand_color_help} />
          </Form>
        )}
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

function convertToValidHex(hex: string): string {
  if (`${hex}`.length != 6) return null;
  return hex;
}

function presentHex(hex: string): string {
  return convertToValidHex(hex) ? hex : language.brand_no_color;
}

export default Branding;
